import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text, Line, Arrow, Group, Circle } from 'react-konva';
import { useTranslation } from 'react-i18next';
import tireImageSrc from '../../../images/pneumatico.png'; // Import tire image
import texture1Image from '../../../images/asfalto.png';
import texture2Image from '../../../images/graniglia-pav.png';
import texture4Image from '../../../images/subgrade.png';

// Interfacce per le Props (come prima)
export interface ILayerData {
    name: string;
    thickness: number;
    color: string;
}

export interface IGeogridInfo {
    positionFromBottomOfBase: number; // Dal fondo del Base Course
    label?: string;
    color?: string;
}

/**
 * Props per TireAndLayers: rappresenta la sezione trasversale con quattro strati (surface, base, subbase, subgrade) e le linee opzionali di geogrid/geotextile.
 * - Supporta sia la nuova interfaccia a 4 strati per progetti pavimentati che la vecchia interfaccia a 2 strati per progetti unpaved (backward compatibility).
 * - geogrid e geotextile sono sempre supportati tramite le rispettive props.
 */
interface Props {
    // Nuova interfaccia 4-layer (progetti paved)
    surfaceLayer?: ILayerData;
    baseLayer?: ILayerData;
    subbaseLayer?: ILayerData;
    // Subgrade è comune a entrambe le interfacce
    subgrade: ILayerData; // Spessore = altezza visibile

    // Vecchia interfaccia 2-layer (progetti unpaved - backward compatibility)
    baseCourse?: ILayerData;

    leftDimensionValue?: number;
    rightDimensionValue?: number;
    savingPercentage?: number;
    geogrid?: IGeogridInfo;
    subbaseGeogrid?: IGeogridInfo; // Seconda geogrid per il subbase
    unitLabel?: string;
    /** Metres in one unit of the thickness values (0.3048 for ft): keeps the drawing scale per metre in every unit */
    metersPerUnit?: number;
    showLeftDimension?: boolean;
    showRightDimension?: boolean;
    showSavingBox?: boolean;
    showGeogrid?: boolean;
    showSubbaseGeogrid?: boolean; // Flag per mostrare la seconda geogrid
    showTire?: boolean;
    showLayers?: boolean;
    showGeotextile?: boolean;
    leftDimensionStrokeWidth?: number;
    horizontalSpacing?: number;
    verticalSpacing?: number;
    customStageHeight?: number;
    onStageHeightCalculated?: (height: number) => void;
    onSubgradeYCalculated?: (y: number) => void;
    alignFromBottom?: boolean;
    targetSubgradeY?: number;
    hideLayerTitles?: boolean;
    projectType?: 'container_yard' | 'parking_deck' | string;
    forcedStageWidth?: number;
    rectWidth?: number; // Larghezza dei rettangoli dei layer, opzionale per personalizzazione
    rectStartXOffset?: number;
    stageType?: 'geogrid' | 'geotextile' | 'unstabilized' | string;
    unstabilizedStageHeight?: number;
}

// --- Componente ---
const TireAndLayersAirport: React.FC<Props> = ({
    surfaceLayer,
    baseLayer,
    subbaseLayer,
    subgrade,
    baseCourse, // backward compatibility per progetti unpaved
    leftDimensionValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rightDimensionValue,
    savingPercentage,
    geogrid,
    subbaseGeogrid, // Seconda geogrid per il subbase
    unitLabel = 'm',
    metersPerUnit = 1,
    showLeftDimension = false,
    showRightDimension = true,
    showSavingBox = false,
    showGeogrid = false,
    showSubbaseGeogrid = false, // Flag per mostrare la seconda geogrid
    showGeotextile = false,
    showTire = true,
    showLayers = true,
    leftDimensionStrokeWidth,
    customStageHeight,
    onStageHeightCalculated,
    onSubgradeYCalculated,
    alignFromBottom = false,
    targetSubgradeY,
    hideLayerTitles = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    projectType = 'container_yard',
    forcedStageWidth,
    rectWidth = 360, // Larghezza dei rettangoli dei layer, di default 360
    rectStartXOffset = 0, // Offset per la posizione X dei rettangoli
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    stageType,
    unstabilizedStageHeight,
}) => {
    const { t } = useTranslation();
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [texture1Img, setTexture1Img] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);

    // --- Determina modalità di rendering (4-layer vs 2-layer per backward compatibility) ---
    const is4LayerMode = !!(surfaceLayer && baseLayer && subbaseLayer);

    // Per backward compatibility, usa baseCourse come base layer se non è specificato il 4-layer mode
    const actualSurfaceLayer = surfaceLayer || (baseCourse ? { name: t('surface_layer', 'Surface'), thickness: 0.1, color: '#2C2C2C' } : { name: '', thickness: 0, color: '' });
    const actualBaseLayer = baseLayer || baseCourse || { name: '', thickness: 0, color: '' };
    const actualSubbaseLayer = subbaseLayer || (baseCourse ? { name: t('subbase_layer', 'Subbase'), thickness: 0.1, color: '#F5DEB3' } : { name: '', thickness: 0, color: '' });

    // --- Calcoli Dimensioni e Posizioni ---
    const scale = 75 * metersPerUnit; // Dimezzato da 150 a 75 per ridurre l'altezza mantenendo le proporzioni
    const surfaceHeightPx = is4LayerMode ? Math.max(actualSurfaceLayer.thickness * scale, 1) : 0;
    const baseHeightPx = Math.max(actualBaseLayer.thickness * scale, 1);
    const subbaseHeightPx = is4LayerMode ? Math.max(actualSubbaseLayer.thickness * scale, 1) : 0;
    const subgradeHeightPx = Math.max(subgrade.thickness * scale, 1);
    const stageTopMargin = 10;
    const stageBottomMargin = 10;
    const totalLayerHeight = surfaceHeightPx + baseHeightPx + subbaseHeightPx + subgradeHeightPx;
    const titleAreaWidth = 100;
    const leftDimAreaWidth = 50;
    const squareWidth = rectWidth; // Larghezza rettangoli livelli
    const rectStartX = titleAreaWidth + rectStartXOffset;
    const rectEndX = rectStartX + squareWidth;
    const rightDimAreaWidth_aprx = 80;
    const savingBoxWidth = 160;
    const savingBoxHeight = 70;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const savingBoxAreaWidth = savingBoxWidth + 15;
    const stageWidth = rectWidth + titleAreaWidth + rightDimAreaWidth_aprx;
    const TIRE_IMAGE_HEIGHT = 150;
    const TIRE_RAPPORTO_DIAGONALE = 2.2;
    const TIRE_IMAGE_WIDTH = TIRE_IMAGE_HEIGHT / TIRE_RAPPORTO_DIAGONALE;
    const currentImageHeight = TIRE_IMAGE_HEIGHT;
    const currentImageWidth = TIRE_IMAGE_WIDTH;
    const tireImageY = stageTopMargin;

    let surfaceY: number, baseY: number, subbaseY: number, subgradeY: number, stageHeight: number;

    // === QUOTE (DIMENSIONI) ===
    const dimColor = '#004652';
    const marrone = '#9e480e';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const profiloSaving = 3;
    const dimStrokeWidth = 2;
    const thickDimLineWidth = 22;
    const thickDimLineSpacing = 16;
    const effectiveLeftDimStrokeWidth = leftDimensionStrokeWidth ?? dimStrokeWidth;
    const extensionLineOffset = 0;
    const extensionLineLength = 30;
    const dimensionLineOffsetX = 16;
    const textOffsetY = -6;
    const dimTextProps = { fontSize: 12, fill: 'black', fontStyle: 'bold' };
    const dimArrowProps = { stroke: marrone, fill: marrone, strokeWidth: 1, pointerLength: 9, pointerWidth: 8 };
    const leftDimLineX = rectStartX - extensionLineOffset - dimensionLineOffsetX;
    const rightDimLineX = rectEndX + extensionLineOffset + dimensionLineOffsetX;
    const savingLabelOffsetY = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const stageRightMargin = 10;
    const savingBoxY = stageTopMargin + Math.abs(savingLabelOffsetY);
    const geogridColor = geogrid?.color || '#24244f';
    const layerStrokeColor = '#444444';
    const layerStrokeWidth = 0.5;

    // Ref e stato per larghezza/altezza testo geogrid
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const geogridTextRef = useRef<any>(null);
    const [geogridTextWidth, setGeogridTextWidth] = useState<number>(0);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [geogridTextHeight, setGeogridTextHeight] = useState<number>(0);

    useEffect(() => {
        if (geogridTextRef.current && geogrid?.label) {
            setGeogridTextWidth(geogridTextRef.current.getTextWidth());
            setGeogridTextHeight(geogridTextRef.current.getTextHeight());
        }
    }, [geogrid?.label]);
    // Ref e stato per larghezza/altezza testo subbaseGeogrid
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subbaseGeogridTextRef = useRef<any>(null);
    const [subbaseGeogridTextWidth, setSubbaseGeogridTextWidth] = useState<number>(0);
    const [subbaseGeogridTextHeight, setSubbaseGeogridTextHeight] = useState<number>(0);

    useEffect(() => {
        if (subbaseGeogridTextRef.current && subbaseGeogrid?.label) {
            setSubbaseGeogridTextWidth(subbaseGeogridTextRef.current.getTextWidth());
            setSubbaseGeogridTextHeight(subbaseGeogridTextRef.current.getTextHeight());
        }
    }, [subbaseGeogrid?.label]);

    useEffect(() => {
        const imgToLoad = new window.Image();
        imgToLoad.src = tireImageSrc;
        imgToLoad.onload = () => setImage(imgToLoad);
        imgToLoad.onerror = () => {
            // Intentionally empty - silently ignore image load errors
        };
        const tex1 = new window.Image();
        tex1.src = texture1Image;
        tex1.onload = () => setTexture1Img(tex1);
        tex1.onerror = () => {
            // Intentionally empty - silently ignore texture load errors
        };
        const tex2 = new window.Image();
        tex2.src = texture2Image;
        tex2.onload = () => setTexture2Img(tex2);
        tex2.onerror = () => {
            // Intentionally empty - silently ignore texture load errors
        };
        const tex4 = new window.Image();
        tex4.src = texture4Image;
        tex4.onload = () => setTexture4Img(tex4);
        tex4.onerror = () => {
            // Intentionally empty - silently ignore texture load errors
        };
    }, []);

    const geogridLabelPadding = 20;
    // posizine del label geogrid:
    const X_geogridLabelGroup = rectStartX + squareWidth / 2 - (geogridTextWidth + geogridLabelPadding) / 2;
    const Y_geogridLabelGroup = -(geogridTextHeight + geogridLabelPadding);
    // const Y_geogridLabelGroup = 9;
    // posizine del label geoTextTile:
    const X_subbaseLabelGroup = rectStartX + squareWidth / 2 - (subbaseGeogridTextWidth + geogridLabelPadding) / 2;
    const Y_subbaseLabelGroup = -(geogridTextHeight + geogridLabelPadding);
    //const Y_subbaseLabelGroup = 9;

    //////// custom riga con pallini
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const y = 25; // fissa Y come nel tuo esempio
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const canvasWidth = 420;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const canvasHeight = 52;
    const nPoints = 6;
    const cerchioRaggio = 6;
    const circleSpacing = (rectEndX - (rectStartX + 0)) / (nPoints - 1);
    const circleXs = Array.from({ length: nPoints }, (_, i) => rectStartX - 6 + i * circleSpacing);

    if (alignFromBottom && targetSubgradeY !== undefined) {
        subgradeY = targetSubgradeY;
        if (is4LayerMode) {
            subbaseY = subgradeY - subbaseHeightPx;
            baseY = subbaseY - baseHeightPx;
            surfaceY = baseY - surfaceHeightPx;
        } else {
            // Modalità 2-layer (backward compatibility)
            baseY = subgradeY - baseHeightPx;
            surfaceY = baseY;
            subbaseY = baseY;
        }
        const topSpaceForImage = currentImageHeight + stageTopMargin;
        const layersEndY = subgradeY + subgradeHeightPx;
        stageHeight = customStageHeight ?? Math.max(layersEndY + stageBottomMargin, topSpaceForImage + totalLayerHeight + stageBottomMargin);
    } else {
        if (is4LayerMode) {
            surfaceY = tireImageY + currentImageHeight;
            baseY = surfaceY + surfaceHeightPx;
            subbaseY = baseY + baseHeightPx;
            subgradeY = subbaseY + subbaseHeightPx;
        } else {
            // Modalità 2-layer (backward compatibility)
            baseY = tireImageY + currentImageHeight;
            subgradeY = baseY + baseHeightPx;
            surfaceY = baseY;
            subbaseY = baseY;
        }
        stageHeight = customStageHeight ?? (is4LayerMode ? surfaceY + totalLayerHeight + stageBottomMargin : baseY + totalLayerHeight + stageBottomMargin);
    }
    // --- Aggiunta: considera anche il box saving se visibile --- allora aumenta l'altezza
    // Calcolo differenza di altezza tra unstabilized e stabilized pr farli uguali
    let savingBoxOffset = 0;
    if (showSavingBox && typeof unstabilizedStageHeight === 'number') {
        const stabilizedStageHeight = is4LayerMode ? (customStageHeight ?? surfaceY + totalLayerHeight + stageBottomMargin) : (customStageHeight ?? baseY + totalLayerHeight + stageBottomMargin);
        const heightDiff = unstabilizedStageHeight - stabilizedStageHeight;
        // Usa solo la differenza, anche se negativa
        savingBoxOffset = heightDiff;
    }

    if (showSavingBox) {
        // Per il terzo canvas "STABILIZED" vogliamo allineare il subgrade al bottom
        // e costruire gli strati verso l'alto, mantenendo il pneumatico in alto
        if (is4LayerMode) {
            // Calcola la posizione del subgrade allineata agli altri canvas
            // Se abbiamo targetSubgradeY, usiamo quella, altrimenti calcoliamo
            if (alignFromBottom && targetSubgradeY !== undefined) {
                subgradeY = targetSubgradeY;
            } else {
                // Allinea il subgrade alla stessa altezza degli altri canvas
                subgradeY = tireImageY + currentImageHeight + savingBoxOffset + surfaceHeightPx + baseHeightPx + subbaseHeightPx;
            }
            // Costruisci gli strati verso l'alto dal subgrade
            subbaseY = subgradeY - subbaseHeightPx;
            baseY = subbaseY - baseHeightPx;
            surfaceY = baseY - surfaceHeightPx;
            stageHeight = customStageHeight ?? subgradeY + subgradeHeightPx + stageBottomMargin;
        } else {
            // Modalità 2-layer (backward compatibility)
            if (alignFromBottom && targetSubgradeY !== undefined) {
                subgradeY = targetSubgradeY;
            } else {
                subgradeY = tireImageY + currentImageHeight + savingBoxOffset + baseHeightPx;
            }
            baseY = subgradeY - baseHeightPx;
            surfaceY = baseY;
            subbaseY = baseY;
            stageHeight = customStageHeight ?? subgradeY + subgradeHeightPx + stageBottomMargin;
        }
    }

    useEffect(() => {
        if (onStageHeightCalculated) onStageHeightCalculated(stageHeight);
        if (onSubgradeYCalculated) onSubgradeYCalculated(subgradeY);
    }, [stageHeight, subgradeY, onStageHeightCalculated, onSubgradeYCalculated]);

    // Dopo il calcolo di stageHeight

    return (
        <Stage width={forcedStageWidth ?? stageWidth} height={stageHeight}>
            <Layer>
                <Rect // questo mi mostra la dimensione dello stage
                    x={0}
                    y={0}
                    width={stageWidth}
                    height={stageHeight}
                    // fill="#fffec8"
                />
                {/* === la condizione si verifica se nascondere i titoli è false, cioè da vedersi hide = false === */}
                {!hideLayerTitles && showLayers && (
                    <>
                        {is4LayerMode && (
                            <Text
                                text={t(actualSurfaceLayer.name) ?? ''}
                                x={rectStartX - titleAreaWidth}
                                y={surfaceY + surfaceHeightPx / 2 + textOffsetY}
                                width={titleAreaWidth - 15}
                                align="right"
                                fontSize={12}
                                fill="black"
                            />
                        )}
                        <Text
                            text={t(actualBaseLayer.name) ?? ''}
                            x={rectStartX - titleAreaWidth}
                            y={baseY + baseHeightPx / 2 + textOffsetY}
                            width={titleAreaWidth - 15}
                            align="right"
                            fontSize={12}
                            fill="black"
                        />
                    </>
                )}
                {/* === mostra gli strati della larghezza standard: 360 === */}
                {showLayers && (
                    <>
                        <Rect x={0} y={0} width={stageWidth} height={stageHeight} />
                        {/* Surface Layer (solo in modalità 4-layer) */}
                        {is4LayerMode && (
                            <>
                                <Rect
                                    x={rectStartX}
                                    y={surfaceY}
                                    width={squareWidth}
                                    height={surfaceHeightPx}
                                    fillPatternImage={texture1Img || undefined}
                                    fillPatternRepeat="repeat"
                                    fillPatternScale={{ x: 0.2, y: 0.2 }}
                                    stroke={layerStrokeColor}
                                    strokeWidth={layerStrokeWidth}
                                    opacity={1}
                                />
                                {/* Bordo superiore 3px sopra il surface layer */}
                                {/* <Line points={[rectStartX, surfaceY, rectEndX, surfaceY]} stroke='#e4cab7' strokeWidth={3} /> */}
                            </>
                        )}
                        {/* Base Layer */}
                        <Rect
                            x={rectStartX}
                            y={baseY}
                            width={squareWidth}
                            height={baseHeightPx}
                            fillPatternImage={texture2Img || undefined}
                            fillPatternRepeat="repeat-y"
                            fillPatternScale={{ x: 1, y: 1 }}
                            stroke={layerStrokeColor}
                            strokeWidth={layerStrokeWidth}
                            opacity={1}
                        />
                        {/* Bordo superiore per base layer se non c'è surface layer */}
                        {!is4LayerMode && <Line points={[rectStartX, baseY, rectEndX, baseY]} stroke="#e4cab7" strokeWidth={3} />}
                        {/* Subbase Layer (solo in modalità 4-layer) */}
                        {is4LayerMode && (
                            <Rect
                                x={rectStartX}
                                y={subbaseY}
                                width={squareWidth}
                                height={subbaseHeightPx}
                                fillPatternImage={texture2Img || undefined}
                                fillPatternRepeat="repeat-y"
                                fillPatternScale={{ x: 0.8, y: 0.8 }}
                                stroke={layerStrokeColor}
                                strokeWidth={layerStrokeWidth}
                                opacity={1}
                            />
                        )}
                        {/* Subgrade */}
                        <Rect
                            x={rectStartX}
                            y={subgradeY}
                            width={squareWidth}
                            height={subgradeHeightPx}
                            fillPatternImage={texture4Img || undefined}
                            fillPatternRepeat="repeat"
                            fillPatternScale={{ x: 0.5, y: 0.5 }}
                            stroke={layerStrokeColor}
                            strokeWidth={layerStrokeWidth}
                        />
                    </>
                )}
                {/* === Linea Geogriglia (Condizionale) verde === */}
                {showLayers &&
                    (showGeogrid || showSavingBox) &&
                    geogrid &&
                    typeof geogrid.positionFromBottomOfBase === 'number' &&
                    (() => {
                        return true;
                    })() && (
                        // Sposta la geogriglia leggermente più in alto per rendere visibile la linea di confine base/subbase
                        <Group y={baseY + baseHeightPx + geogrid.positionFromBottomOfBase} x={0}>
                            <Line
                                points={[rectStartX, 0, rectEndX, 0]}
                                stroke={geogridColor}
                                strokeWidth={3}
                                shadowColor="#FFFFFF"
                                // shadowBlur={8}
                                opacity={0.8}
                                lineCap="round"
                            />
                            {/* Cerchietti */}
                            {circleXs.map((x, idx) => (
                                <Circle
                                    key={idx}
                                    x={x + 6}
                                    y={0}
                                    radius={cerchioRaggio}
                                    fill="white"
                                    stroke={geogridColor}
                                    strokeWidth={2}
                                    shadowColor="#FFFFFF"
                                    // shadowBlur={8}
                                    opacity={1}
                                />
                            ))}
                            {geogrid.label && (
                                <Group x={X_geogridLabelGroup} y={Y_geogridLabelGroup}>
                                    <Rect
                                        width={geogridTextWidth + geogridLabelPadding}
                                        height={geogridTextHeight + geogridLabelPadding / 2}
                                        fill="#FFFFFF"
                                        cornerRadius={6}
                                        x={0}
                                        y={-1}
                                        stroke="#828282"
                                        strokeWidth={1}
                                    />
                                    <Text ref={geogridTextRef} text={geogrid.label} fontSize={12} fontStyle="bold" fill={'black'} x={geogridLabelPadding / 2} y={5} />
                                </Group>
                            )}
                        </Group>
                    )}
                {/* === Linea Geogrid Subbase (Condizionale) arancione === */}
                {showLayers &&
                    (showSubbaseGeogrid || showSavingBox) &&
                    subbaseGeogrid &&
                    typeof subbaseGeogrid.positionFromBottomOfBase === 'number' &&
                    (() => {
                        return true;
                    })() && (
                        // Sposta la geogriglia subbase leggermente più in alto per rendere visibile la linea di confine subbase/subgrade
                        <Group y={subbaseY + subbaseHeightPx + subbaseGeogrid.positionFromBottomOfBase} x={0}>
                            <Line
                                points={[rectStartX, 0, rectEndX, 0]}
                                stroke={geogridColor}
                                strokeWidth={3}
                                shadowColor="#FFFFFF"
                                // shadowBlur={8}
                                opacity={0.8}
                                lineCap="round"
                            />
                            {/* Cerchietti */}
                            {circleXs.map((x, idx) => (
                                <Circle
                                    key={idx}
                                    x={x + 6}
                                    y={0}
                                    radius={cerchioRaggio}
                                    fill="white"
                                    stroke={geogridColor}
                                    strokeWidth={2}
                                    shadowColor="#FFFFFF"
                                    // shadowBlur={8}
                                    opacity={1}
                                />
                            ))}
                            {subbaseGeogrid.label && (
                                <Group x={X_subbaseLabelGroup} y={Y_subbaseLabelGroup}>
                                    <Rect
                                        width={subbaseGeogridTextWidth + geogridLabelPadding}
                                        height={subbaseGeogridTextHeight + geogridLabelPadding / 2}
                                        fill="#FFFFFF"
                                        cornerRadius={6}
                                        x={0}
                                        y={-1}
                                        stroke="#828282"
                                        strokeWidth={1}
                                    />
                                    <Text ref={subbaseGeogridTextRef} text={subbaseGeogrid.label} fontSize={12} fontStyle="bold" fill={'black'} x={geogridLabelPadding / 2} y={5} />
                                </Group>
                            )}
                        </Group>
                    )}
                {/* === Linea Geotextile  (Condizionale) Rossa === */}
                {showLayers && showGeotextile && (
                    <Group y={subgradeY} x={10}>
                        <Line
                            points={[rectStartX - 11, 0, rectEndX - 5, 0]}
                            stroke="#FF0000"
                            strokeWidth={4}
                            dash={[15, 11]} // tratteggio: 8px linea, 6px spazio
                            shadowColor="#FFB0B0"
                            opacity={0.8}
                            lineCap="round"
                        />
                        {/* {circleXs.map((x, idx) => (
                            <Circle key={idx} x={x} y={0} radius={cerchioRaggio} fill='white' stroke='#FF0000' strokeWidth={2} shadowColor='#FFB0B0' opacity={1} />
                        ))} */}
                        <Group x={rectStartX + 10} y={10}>
                            <Rect width={'Geotextile'.length * 6.4 + 10} height={22} fill="#FFFFFF" cornerRadius={6} x={0} y={-1} stroke="#828282" strokeWidth={2} />
                            <Text text="Geotextile" fontSize={14} fontStyle="normal" fill="black" x={5} y={3} />
                        </Group>
                    </Group>
                )}
                {/* === container/tire (Condizionale) === */}
                {showLayers && showTire && image && (
                    <KonvaImage
                        image={image}
                        x={rectStartX + squareWidth / 2 - currentImageWidth / 2}
                        y={is4LayerMode ? surfaceY - currentImageHeight : baseY - currentImageHeight}
                        width={currentImageWidth}
                        height={currentImageHeight}
                    />
                )}
                {/* === Linee di Quota (Condizionali) === */}
                {showLeftDimension && typeof leftDimensionValue === 'number' && leftDimensionValue > 0 && (
                    <Group>
                        <Rect x={leftDimLineX + thickDimLineSpacing} y={surfaceY} width={thickDimLineWidth} height={totalLayerHeight} fill={'dimColor'} />
                        <Line points={[rectStartX - extensionLineOffset, surfaceY, rectStartX - extensionLineOffset - extensionLineLength, surfaceY]} stroke={dimColor} strokeWidth={2} dash={[3, 2]} />
                        <Line
                            points={[rectStartX - extensionLineOffset, subgradeY + subgradeHeightPx, rectStartX - extensionLineOffset - extensionLineLength, subgradeY + subgradeHeightPx]}
                            stroke={dimColor}
                            strokeWidth={2}
                            dash={[3, 2]}
                        />
                        <Arrow points={[leftDimLineX, surfaceY, leftDimLineX, subgradeY + subgradeHeightPx]} {...dimArrowProps} strokeWidth={effectiveLeftDimStrokeWidth} pointerAtBeginning={true} />
                        <Text text={`${leftDimensionValue.toFixed(2)} ${unitLabel}`} x={leftDimAreaWidth - 5} y={surfaceY + totalLayerHeight / 2 + textOffsetY} {...dimTextProps} rotation={-90} />
                    </Group>
                )}
                {/* === Quota individuale per ogni layer (solo modalità 4-layer) === */}
                {showRightDimension && is4LayerMode && (
                    <>
                        {/* Surface quota */}
                        {actualSurfaceLayer.thickness > 0 && (
                            <Group>
                                <Line
                                    points={[rectEndX + extensionLineOffset, surfaceY, rectEndX + extensionLineOffset + extensionLineLength, surfaceY]}
                                    stroke={marrone}
                                    strokeWidth={1}
                                    dash={[3, 2]}
                                />
                                <Line points={[rectEndX + extensionLineOffset, baseY, rectEndX + extensionLineOffset + extensionLineLength, baseY]} stroke={marrone} strokeWidth={1} dash={[3, 2]} />
                                <Arrow points={[rightDimLineX, surfaceY, rightDimLineX, baseY]} {...dimArrowProps} pointerAtBeginning={true} />
                                <Text text={`${actualSurfaceLayer.thickness.toFixed(2)} ${unitLabel}`} x={rightDimLineX + 15} y={(surfaceY + baseY) / 2 + textOffsetY} {...dimTextProps} />
                            </Group>
                        )}
                        {/* Base quota */}
                        {actualBaseLayer.thickness > 0 && (
                            <Group>
                                <Line points={[rectEndX + extensionLineOffset, baseY, rectEndX + extensionLineOffset + extensionLineLength, baseY]} stroke={marrone} strokeWidth={1} dash={[3, 2]} />
                                <Line
                                    points={[rectEndX + extensionLineOffset, subbaseY, rectEndX + extensionLineOffset + extensionLineLength, subbaseY]}
                                    stroke={marrone}
                                    strokeWidth={1}
                                    dash={[3, 2]}
                                />
                                <Arrow points={[rightDimLineX, baseY, rightDimLineX, subbaseY]} {...dimArrowProps} pointerAtBeginning={true} />
                                <Text text={`${actualBaseLayer.thickness.toFixed(2)} ${unitLabel}`} x={rightDimLineX + 15} y={(baseY + subbaseY) / 2 + textOffsetY} {...dimTextProps} />
                            </Group>
                        )}
                        {/* Subbase quota */}
                        {actualSubbaseLayer.thickness > 0 && (
                            <Group>
                                <Line
                                    points={[rectEndX + extensionLineOffset, subbaseY, rectEndX + extensionLineOffset + extensionLineLength, subbaseY]}
                                    stroke={marrone}
                                    strokeWidth={1}
                                    dash={[3, 2]}
                                />
                                <Line
                                    points={[rectEndX + extensionLineOffset, subgradeY, rectEndX + extensionLineOffset + extensionLineLength, subgradeY]}
                                    stroke={marrone}
                                    strokeWidth={1}
                                    dash={[3, 2]}
                                />
                                <Arrow points={[rightDimLineX, subbaseY, rightDimLineX, subgradeY]} {...dimArrowProps} pointerAtBeginning={true} />
                                <Text text={`${actualSubbaseLayer.thickness.toFixed(2)} ${unitLabel}`} x={rightDimLineX + 15} y={(subbaseY + subgradeY) / 2 + textOffsetY} {...dimTextProps} />
                            </Group>
                        )}
                    </>
                )}
                {/* === Box Saving Percentage (Condizionale) === */}
                {showLayers && showSavingBox && typeof savingPercentage === 'number' && savingPercentage > 0 && (
                    <Group x={rectStartX + squareWidth - 55} y={savingBoxY + 20} width={savingBoxWidth}>
                        <Rect width={savingBoxWidth} height={savingBoxHeight} fill="white" stroke="#004652" strokeWidth={3} cornerRadius={4} />
                        <Text text={t('thickness_saving') ?? 'Saving'} x={0} y={12} width={savingBoxWidth} align="center" fontSize={15} fill={dimColor} fontStyle="italic" />
                        <Text
                            text={`-${savingPercentage.toFixed(0)}%`}
                            width={savingBoxWidth}
                            height={savingBoxHeight}
                            align="center"
                            y={-8}
                            verticalAlign="bottom"
                            fontSize={30}
                            fill="#004652"
                            fontStyle="bold"
                        />
                    </Group>
                )}
            </Layer>
        </Stage>
    );
};
export default TireAndLayersAirport;
